/**
 * DOM helpers for the print-via-iframe pipeline.
 *
 * Mirrors `jackhammer-ui/src/lib/reportRenderer/dom.js` but typed and
 * Eloquor-flavored: pulls the verdict color trio (hue / soft / ink) from
 * the live `--rt-{verdict}-*` CSS variables so a brand change in
 * `colors_and_type.css` re-skins the PDF for free.
 */

import type { VerdictCode } from '@eloquor/api-contract';
import { PRINT_STYLE } from './constants';

export function writeReportDocument(doc: Document, title: string): void {
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(
    title
  )}</title></head><body></body></html>`);
  doc.close();
}

export function injectStylesheet(doc: Document, href: string): void {
  const link = doc.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  doc.head.appendChild(link);
}

export function injectPrintStyle(doc: Document): void {
  const style = doc.createElement('style');
  style.textContent = PRINT_STYLE;
  doc.head.appendChild(style);
}

/** Resolve a verdict's three colors (hue / soft / ink) from CSS variables. */
export function resolveTokenColors(verdict: VerdictCode): {
  hue: string;
  soft: string;
  ink: string;
} {
  const root = document.documentElement;
  const cs = getComputedStyle(root);
  return {
    hue: cs.getPropertyValue(`--rt-${verdict}`).trim() || '#6A6F7C',
    soft: cs.getPropertyValue(`--rt-${verdict}-soft`).trim() || 'transparent',
    ink: cs.getPropertyValue(`--rt-${verdict}-ink`).trim() || '#0F1115',
  };
}

/**
 * Wait until the iframe has loaded webfonts (best-effort; resolves either
 * way after `timeoutMs`). Prevents the print dialog from firing before
 * Carlito is ready, which would substitute a system sans.
 */
export function ensureReportFonts(doc: Document, timeoutMs = 1200): Promise<void> {
  return new Promise((resolve) => {
    const t = setTimeout(resolve, timeoutMs);
    const fonts = (doc as Document & { fonts?: FontFaceSet }).fonts;
    if (!fonts || typeof fonts.ready?.then !== 'function') {
      clearTimeout(t);
      resolve();
      return;
    }
    fonts.ready
      .then(() => {
        clearTimeout(t);
        resolve();
      })
      .catch(() => {
        clearTimeout(t);
        resolve();
      });
  });
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
